import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Add, ArchiveOutlined, ArrowForward, Inventory2Outlined, Search, UnarchiveOutlined } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ExperimentalPopupProvider from '../ExperimentalPopups/ExperimentalPopupProvider';
import { previewLists } from './listasPreviewData';
import './ShoppingListsV2.css';

const progressFor = (list) => {
  const total = list.ingredientes?.length || 0;
  const bought = list.ingredientes?.filter((item) => item.estado === 'comprado').length || 0;
  return { total, bought, percent: total ? Math.round((bought / total) * 100) : 0 };
};

export default function ShoppingListsV2({ preview = false }) {
  const { authToken } = useAuth();
  const [lists, setLists] = useState(preview ? previewLists : []);
  const [loading, setLoading] = useState(!preview);
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (preview) return;
    const load = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/listascompra`, { headers: { Authorization: `Bearer ${authToken}` } });
        if (!response.ok) throw new Error('No se pudieron cargar las listas');
        const data = await response.json();
        setLists(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    load();
  }, [authToken, preview]);

  const handleArchive = async (list) => {
    const archived = !list.archived;
    setLists((current) => current.map((item) => item._id === list._id ? { ...item, archived } : item));
    if (preview) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/listascompra/archive/${list._id}`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ archived }),
      });
      if (!response.ok) throw new Error('No se pudo actualizar la lista');
    } catch (error) {
      console.error(error);
      setLists((current) => current.map((item) => item._id === list._id ? { ...item, archived: list.archived } : item));
    }
  };

  const active = lists.filter((list) => !list.archived);
  const filtered = useMemo(() => lists.filter((list) => (tab === 'active' ? !list.archived : list.archived)
    && list.nombre.toLowerCase().includes(search.toLowerCase())), [lists, search, tab]);

  return (
    <ExperimentalPopupProvider>
      <main className="lists-v2-page">
        <section className="lists-v2-hero">
          <div>
            <span className="lists-v2-eyebrow">Tus compras, sin vueltas</span>
            <h1>Listas de compra</h1>
            <p>Todo lo que necesitas, ordenado para comprar más rápido.</p>
          </div>
          <Link className="lists-v2-primary" to={preview ? '/preview/listas/crear-lista' : '/listas/crear-lista'}><Add /> Crear lista</Link>
        </section>

        <section className="lists-v2-toolbar">
          <div className="lists-v2-tabs" role="tablist">
            <button className={tab === 'active' ? 'is-active' : ''} onClick={() => setTab('active')}>Activas <span>{active.length}</span></button>
            <button className={tab === 'archived' ? 'is-active' : ''} onClick={() => setTab('archived')}>Archivadas <span>{lists.length - active.length}</span></button>
          </div>
          <label className="lists-v2-search"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar una lista" /></label>
        </section>

        {loading ? <div className="lists-v2-empty">Cargando tus listas…</div> : filtered.length ? (
          <section className="lists-v2-grid">
            {filtered.map((list) => {
              const stats = progressFor(list);
              return <article className={`lists-v2-card ${stats.percent === 100 ? 'is-complete' : ''}`} key={list._id}>
                <div className="lists-v2-card-top">
                  <div className="lists-v2-card-icon"><Inventory2Outlined /></div>
                  <button className="lists-v2-icon-button" onClick={() => handleArchive(list)} aria-label={list.archived ? 'Desarchivar' : 'Archivar'}>{list.archived ? <UnarchiveOutlined /> : <ArchiveOutlined />}</button>
                </div>
                <div className="lists-v2-card-copy"><span>{new Date(list.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span><h2>{list.nombre}</h2></div>
                <div className="lists-v2-progress-copy"><span>{stats.percent === 100 ? 'Compra completada' : `${stats.total - stats.bought} por comprar`}</span><strong>{stats.bought}/{stats.total}</strong></div>
                <div className="lists-v2-progress"><span style={{ width: `${stats.percent}%` }} /></div>
                <Link className="lists-v2-open" to={preview ? '/preview/listas/detalle' : `/listas/${list._id}`}>Abrir lista <ArrowForward /></Link>
              </article>;
            })}
          </section>
        ) : <div className="lists-v2-empty"><Inventory2Outlined /><h2>{search ? 'No encontramos esa lista' : `No tienes listas ${tab === 'active' ? 'activas' : 'archivadas'}`}</h2><p>{search ? 'Prueba con otro nombre.' : 'Cuando tengas alguna, aparecerá aquí.'}</p></div>}
      </main>
    </ExperimentalPopupProvider>
  );
}
